import React from 'react';
import styled from 'styled-components';
import { DatePicker, Icon, notification, Button } from 'antd';
import { connect } from 'react-redux';
import firebase from 'firebase';
import moment from 'moment';
import { push } from 'connected-react-router';
import ChartLegend from 'components/ChartLegend';
import RightSidebar from 'components/RightSidebar';
import DownloadChartModal from 'components/DownloadChartModal';
import TitleView from 'components/TitleView';
import chartColors from 'utils/chartcolors';
import mixpanel from 'mixpanel-browser';
import cognitesdk from '@cognite/cognitesdk';
import { TagSelectContainer } from 'containers';
import {
  cogniteloader,
  getSubdomain,
  getGranularity,
} from 'utils/cogniteloader';
import ChartComponent from './ChartComponent';
import EditTagSidebar from './EditTagSidebar';
import ViewEventSidebar from './ViewEventSidebar';
import dateRanges from './DateRanges';

const { RangePicker } = DatePicker;

const DEFAULT_CHART_TITLE = 'CLICK TO EDIT CHART TITLE';

const anomalyAnnotations = [
  [1410166860000, 1410794280000],
  [1414956120000, 1414956720000],
  [1422653460000, 1422654960000],
  [1437419160000, 1437420600000],
  [1437420900000, 1437421860000],
  [1437423540000, 1437429240000],
  [1437500280000, 1437508260000],
  [1437522840000, 1437532860000],
].map(t => ({
  id: t[0],
  data: t,
  startTime: t[0],
  endTime: t[1],
  type: 'anomaly',
  description: "Anomaly found by Cognite's anomaly detection.",
  additionalData: {},
}));

const Container = styled.div`
  margin: -16px;
  display: flex;
  flex-direction: column;
  background: white;
`;

const Topbar = styled.div`
  background: rgb(67, 75, 87);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionWrapper = styled.div`
  float: right;
  margin-right: 15px;
  display: flex;
  align-items: center;
`;

const ActionItemWrapper = styled.div`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  margin-left: 5px;
  margin-right: 5px;
  padding: 5px;
  font-size: 0.8rem;
  white-space: nowrap;
  background: ${props => (props.active ? '#00253a' : 'inherit')};

  i {
    font-size: 1.5rem;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
`;

const ChartContainer = styled.div`
  width: 80%;
  margin-left: 30px;
  margin-right: 30px;
  margin-top: 10px;
`;

const LegendWrapper = styled.div`
  width: 20%;
`;

class ChartView extends React.Component {
  constructor(props) {
    super(props);
    const { match: { params: { chartId } } } = props;
    if (chartId !== 'new') {
      mixpanel.track('Viewed chart', {
        id: chartId,
      });
    }
    this.state = {
      range: {
        start: moment('2017-11-01').startOf('day'),
        end: moment('2017-12-01').endOf('day'),
      },
      chartWidth: 500,
      chartHeight: 500,
      chartId: chartId === 'new' ? null : chartId,
      tags: [],
      loader: cogniteloader(),
      colors: {},
      legendValues: {},
      showEditLegendModal: false,
      selectedLegendTag: {},
      chartTitle: DEFAULT_CHART_TITLE,
      sidebarToggle: null,
      editable: false,
      downloadChartModal: false,
      hiddenSeries: {},
      axisConfig: {},
      showEvents: false,
      eventsEnabled: false,
      anomaliesEnabled: false,
    };
  }

  async componentDidMount() {
    setTimeout(() => {
      this.setState({
        chartWidth: this.chartRef.clientWidth,
        chartHeight: Math.floor(
          window.innerHeight - this.chartRef.getBoundingClientRect().y - 10
        ),
      });
    }, 0);

    window.addEventListener('resize', this.updateFromResize);
    if (this.state.chartId) {
      if (
        this.state.chartId === '-L7iaqgk71qG4n38Zmz1' ||
        this.state.chartId === '-L7ur0b8vowrkpObkKPx'
      ) {
        const res = await cognitesdk.get(
          `api/0.4/projects/akerbp/events?tagId=18-UB-230`
        );
        const events = res.items.filter(
          e => e.startTime && e.endTime && e.endTime > e.startTime
        );
        this.setState({
          annotations: events.map(e => ({
            ...e,
            data: [e.startTime, e.endTime],
            color: 'steelblue',
          })),
          eventsEnabled: true,
        });
      }
      firebase
        .database()
        .ref('charts')
        .child(this.state.chartId)
        .once('value', snap => {
          const chart = snap.val();
          const tags = chart.tags || [];
          const colors = {};
          const axisConfig = {};
          const hiddenSeries = {};
          const strokeWidths = {};
          tags.forEach((t, i) => {
            colors[t.value] = chart.colors[i];
            axisConfig[t.value] = (chart.axisConfig || {})[i] || {
              static: false,
            };
            hiddenSeries[t.value] = (chart.hiddenSeries || {})[i] || false;
            strokeWidths[t.value] = (chart.strokeWidths || {})[i] || 1;
          });
          this.setState({
            chartTitle: chart.title,
            range: {
              start: moment(chart.domain[0]),
              end: moment(chart.domain[1]),
            },
            tags,
            colors,
            strokeWidths,
            editable: chart.creator.uid === this.props.auth.uid,
            loader: cogniteloader(tags),
            hiddenSeries: hiddenSeries || {},
            axisConfig: axisConfig || {},
          });
        });
    } else {
      // New chart
      this.setState({ editable: true });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateFromResize);
  }

  onChangeTitle = e => {
    const chartTitle = e.target.value.toUpperCase();
    this.setState({
      chartTitle,
    });
  };

  onTitleInputClick = e => {
    const chartTitle = e.target.value.toUpperCase();
    if (chartTitle === DEFAULT_CHART_TITLE) {
      e.target.select();
    }
  };

  onSave = async e => {
    e.preventDefault();
    if (this.state.tags.length === 0) {
      notification.error({
        message: 'Error',
        description: 'You need to add some tags to save the chart.',
      });
      return;
    }
    if (
      this.state.chartTitle === DEFAULT_CHART_TITLE ||
      this.state.chartTitle.length === 0
    ) {
      notification.error({
        message: 'Error',
        description: 'Edit the title of the chart before saving.',
      });
      return;
    }
    const chart = {
      title: this.state.chartTitle,
      subDomain: getSubdomain(),
      domain: [+this.state.range.start, +this.state.range.end],
      tags: this.state.tags,
      colors: this.state.tags.map(t => this.state.colors[t.value]),
      creator: {
        uid: this.props.auth.uid,
        email: this.props.auth.email,
      },
      updatedAt: firebase.database.ServerValue.TIMESTAMP,
      hiddenSeries: this.state.tags.map(
        t => this.state.hiddenSeries[t.value] || false
      ),
      axisConfig: this.state.tags.map(
        t => this.state.axisConfig[t.value] || { static: false }
      ),
      strokeWidths: this.state.tags.map(
        t => this.state.strokeWidths[t.value] || 1
      ),
    };
    if (this.state.chartId) {
      await firebase
        .database()
        .ref('charts')
        .child(this.state.chartId)
        .update(chart);
      if (this.state.sidebarToggle) {
        this.setState({
          sidebarToggle: null,
        });
      }
    } else {
      chart.createdAt = chart.updatedAt;
      const ref = firebase
        .database()
        .ref('charts')
        .push();
      const updates = {
        [`users/${this.props.auth.uid}/charts/${ref.key}`]: true,
        [`charts/${ref.key}`]: chart,
      };
      await firebase
        .database()
        .ref()
        .update(updates);
      this.setState({
        chartId: ref.key,
      });
      this.props.dispatch(push(`/charts/${ref.key}`));
    }
    notification.success({
      message: 'Saved',
      description: 'Your chart has been saved.',
    });
  };

  onChangeAxisConfig = (tagId, values) => {
    this.setState({
      axisConfig: {
        ...this.state.axisConfig,
        [tagId]: {
          ...this.state.axisConfig[tagId],
          ...values,
        },
      },
    });
  };

  onChangeStrokeWidth = (tagId, value) => {
    this.setState({
      strokeWidths: {
        ...this.state.strokeWidths,
        [tagId]: value,
      },
    });
  };

  onSelectTag = rawTag => {
    if (!rawTag) {
      return;
    }
    const tag = {
      ...rawTag,
      label: rawTag.label.split('(')[0].trim(),
      value: rawTag.value,
      step: rawTag.step || false,
    };
    const tags = [...this.state.tags, tag];
    const loader = cogniteloader(tags);
    const colors = {
      ...this.state.colors,
      [tag.value]: chartColors[tags.length - 1],
    };
    this.setState({
      tags,
      colors,
      loader,
    });
  };

  onClickAnnotation = annotation => {
    this.setState({
      selectedAnnotation: annotation,
      sidebarToggle: 'view-event',
    });
  };

  handleColorChange = (color, tagId) => {
    this.setState({
      colors: {
        ...this.state.colors,
        [tagId]: color.hex,
      },
    });
  };

  removeTag = async tagId => {
    const tags = this.state.tags.filter(t => t.value !== tagId);
    const colors = { ...this.state.colors };
    delete colors[tagId];
    const { axisConfig, strokeWidths } = this.state;
    if (axisConfig[tagId]) {
      delete axisConfig[tagId];
    }
    if (strokeWidths[tagId]) {
      delete strokeWidths[tagId];
    }
    this.setState({
      tags,
      colors,
      loader: cogniteloader(tags),
      axisConfig,
      strokeWidths,
    });
  };

  toggleHideTag = tag => {
    const { hiddenSeries } = this.state;
    this.setState({
      hiddenSeries: {
        ...hiddenSeries,
        [tag]: !hiddenSeries[tag],
      },
    });
  };

  updateFromResize = () => {
    this.setState({
      chartWidth: this.chartRef.clientWidth,
      chartHeight: Math.floor(
        window.innerHeight - this.chartRef.getBoundingClientRect().y - 10
      ),
    });
  };

  render() {
    const {
      colors,
      tags,
      chartHeight,
      chartWidth,
      sidebarToggle,
      loader,
      editable,
      range,
      chartTitle,
      legendValues,
      downloadChartModal,
      hiddenSeries,
      axisConfig,
      annotations,
      showEvents,
      selectedAnnotation,
      anomaliesEnabled,
      strokeWidths,
    } = this.state;
    let allAnnotations = [];
    if (showEvents) {
      allAnnotations = annotations;
    }
    if (anomaliesEnabled) {
      allAnnotations = [...allAnnotations, ...anomalyAnnotations];
    }
    return (
      <Container>
        <Topbar>
          <TitleView
            title={chartTitle}
            onChange={this.onChangeTitle}
            editable={editable}
            defaultTitle={DEFAULT_CHART_TITLE}
          />
          <ActionWrapper>
            <ActionItemWrapper>
              <RangePicker
                ranges={dateRanges}
                value={[range.start, range.end]}
                allowClear={false}
                onChange={r => {
                  if (r.length > 1) {
                    this.setState({ range: { start: r[0], end: r[1] } });
                  }
                }}
              />
            </ActionItemWrapper>
            {editable && (
              <ActionItemWrapper
                active={sidebarToggle === 'add-tag'}
                onClick={() =>
                  this.setState({
                    sidebarToggle:
                      sidebarToggle === 'add-tag' ? null : 'add-tag',
                  })
                }
              >
                <Icon type="tags" />
                <div>Add tag</div>
              </ActionItemWrapper>
            )}
            {editable && (
              <ActionItemWrapper
                active={sidebarToggle === 'edit-tag'}
                onClick={() =>
                  this.setState({
                    sidebarToggle:
                      sidebarToggle === 'edit-tag' ? null : 'edit-tag',
                  })
                }
              >
                <Icon type="tags-o" />
                <div>Edit tag</div>
              </ActionItemWrapper>
            )}
            {/* <ActionItemWrapper>
              <Icon type="setting" />
              <div>Settings</div>
            </ActionItemWrapper> */}
            {editable && (
              <ActionItemWrapper onClick={this.onSave}>
                <Icon type="save" />
                <div>Save</div>
              </ActionItemWrapper>
            )}
            <ActionItemWrapper>
              <Icon
                type="cloud-download"
                onClick={() =>
                  this.setState({
                    downloadChartModal: true,
                  })
                }
              />
              <div>Download</div>
            </ActionItemWrapper>
          </ActionWrapper>
        </Topbar>
        <ContentContainer>
          <RightSidebar open={sidebarToggle !== null} width={600}>
            {sidebarToggle === 'view-event' && (
              <ViewEventSidebar
                annotation={selectedAnnotation}
                onClose={() =>
                  this.setState({
                    sidebarToggle: null,
                    selectedAnnotation: null,
                  })
                }
              />
            )}
            {sidebarToggle === 'edit-tag' && (
              <EditTagSidebar
                tags={tags}
                colors={colors}
                handleColorChange={this.handleColorChange}
                removeTag={this.removeTag}
                hiddenSeries={hiddenSeries}
                toggleHide={this.toggleHideTag}
                axisConfig={axisConfig}
                strokeWidths={strokeWidths}
                onChangeAxisConfig={this.onChangeAxisConfig}
                onChangeStrokeWidth={this.onChangeStrokeWidth}
              />
            )}
            {sidebarToggle === 'add-tag' && (
              <div style={{ padding: '10px' }}>
                <TagSelectContainer
                  onSelect={this.onSelectTag}
                  currentTags={tags}
                  removeTag={this.removeTag}
                  colors={colors}
                />
              </div>
            )}
          </RightSidebar>
          <ChartContainer
            innerRef={ref => {
              this.chartRef = ref;
            }}
          >
            <ChartComponent
              colors={colors}
              axisConfig={axisConfig}
              loader={loader}
              key={`${tags.length}_${+range.start}_${+range.end}`}
              height={chartHeight}
              width={chartWidth}
              tags={tags}
              margin={{ left: 10, top: 10, bottom: 0, right: 0 }}
              range={range}
              updateLegendValues={l => {
                this.setState({ legendValues: l });
              }}
              hiddenSeries={hiddenSeries}
              annotations={allAnnotations}
              onClickAnnotation={this.onClickAnnotation}
              strokeWidths={strokeWidths}
            />
          </ChartContainer>
          <LegendWrapper>
            <ChartLegend
              tags={tags}
              colors={colors}
              onClick={() => {}}
              legendValues={legendValues}
              hiddenSeries={hiddenSeries}
            />
            {this.state.eventsEnabled && (
              <Button
                type="primary"
                onClick={() => this.setState({ showEvents: !showEvents })}
              >
                {showEvents ? 'Hide events' : 'Show events'}
              </Button>
            )}
            {this.state.eventsEnabled && (
              <Button
                type="primary"
                onClick={() =>
                  this.setState({ anomaliesEnabled: !anomaliesEnabled })
                }
                style={{ marginLeft: '10px' }}
              >
                {anomaliesEnabled ? 'Hide anomalies' : 'Show anomalies'}
              </Button>
            )}
          </LegendWrapper>
          <DownloadChartModal
            visible={downloadChartModal}
            hideModal={() =>
              this.setState({
                downloadChartModal: false,
              })
            }
            defaultRange={getSubdomain()}
            tags={this.state.tags}
            granularity={getGranularity()}
            project={this.props.project}
          />
        </ContentContainer>
      </Container>
    );
  }
}

ChartView.propTypes = {};

export default connect(state => ({
  auth: state.auth,
  project: state.project,
}))(ChartView);



// WEBPACK FOOTER //
// ./src/components/ChartView/ChartView.js